import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { TypewriterContext } from '../../contexts/TypewriterContext';

// Custom sanitization schema to allow <tag> elements with data-value attribute
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'tag'],
  attributes: {
    ...defaultSchema.attributes,
    tag: ['data-value'],
  },
};

// Hook to safely use TypewriterContext (optional - won't throw if not available)
const useOptionalTypewriterContext = () => {
  return useContext(TypewriterContext);
};

// Base markdown components (without tag)
// Note: Heading sizes reduced for better visual hierarchy in profile/summary contexts
// H1 was text-3xl (30px), now text-xl (20px) - for persona titles
// H2 was text-2xl (24px), now text-lg (18px) - for section headings
// H3 was text-xl (20px), now text-base (16px) with semibold
const baseMarkdownComponents = {
  h1: (props: any) => (
    <h1
      className='!text-xl font-heading !font-semibold text-heading-500 mb-2'
      {...props}
    />
  ),
  h2: (props: any) => (
    <h2
      className='!text-lg font-heading !font-semibold text-primary-600 mb-1'
      {...props}
    />
  ),
  h3: (props: any) => (
    <h3
      className='!text-base font-heading !font-semibold text-heading-500 mb-1'
      {...props}
    />
  ),
  p: (props: any) => (
    <p
      className='text-body-600 font-medium [&:not(:last-child)]:mb-6'
      {...props}
    />
  ),
  ul: (props: any) => (
    <ul className='text-body-500 list-disc pl-5 mb-6 font-medium' {...props} />
  ),
  ol: (props: any) => (
    <ol className='text-body-500 list-disc pl-5 mb-6 font-medium' {...props} />
  ),
};

// Tag component factory - creates a tag component with access to parent state
const createTagComponent = (
  currentSelectedTags: string[],
  handleTagClick: (tagValue: string, updatedTags: string[]) => void
) => {
  return (props: any) => {
    const tagValue =
      props['data-value'] ||
      (typeof props.children === 'string' ? props.children : '');
    const isSelected = currentSelectedTags.includes(tagValue);

    const onClick = () => {
      let updatedTags: string[];

      if (isSelected) {
        // Remove from selection
        updatedTags = currentSelectedTags.filter(t => t !== tagValue);
      } else {
        // Add to selection
        updatedTags = [...currentSelectedTags, tagValue];
      }

      handleTagClick(tagValue, updatedTags);
    };

    return (
      <span
        className={`tag inline-block px-3 py-1 rounded-md cursor-pointer transition-colors mr-2 mb-2 ${isSelected
            ? 'tag-selected bg-primary-500 text-white'
            : 'tag-unselected bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        onClick={onClick}
        {...props}
      >
        {props.children}
      </span>
    );
  };
};

type MarkdownRendererProps = {
  markdown: string;
  onTagChange?: (selectedValue: string, allSelected: string[]) => void;
  selectedTags?: string[];
};

export default function MarkdownRenderer({
  markdown,
  onTagChange,
  selectedTags,
}: MarkdownRendererProps) {
  // Local state for tag selections (if not controlled via props)
  const [localSelectedTags, setLocalSelectedTags] = useState<string[]>(
    selectedTags || []
  );

  // Use controlled or uncontrolled state
  const currentSelectedTags =
    selectedTags !== undefined ? selectedTags : localSelectedTags;

  // Handle tag click
  const handleTagClick = (tagValue: string, updatedTags: string[]) => {
    // Update local state if uncontrolled
    if (selectedTags === undefined) {
      setLocalSelectedTags(updatedTags);
    }

    // Call onChange callback
    if (onTagChange) {
      onTagChange(tagValue, updatedTags);
    }
  };

  // Create components with tag support
  const components = {
    ...baseMarkdownComponents,
    tag: createTagComponent(currentSelectedTags, handleTagClick),
  };

  // Sync local state when selectedTags prop changes
  useEffect(() => {
    if (selectedTags !== undefined) {
      setLocalSelectedTags(selectedTags);
    }
  }, [selectedTags]);

  return (
    <div className='prose prose-neutral max-w-none prose-h1:text-xl prose-h1:font-semibold prose-h2:text-lg prose-h2:font-semibold prose-h3:text-base prose-h3:font-semibold prose-h1:mb-2 prose-h2:mb-1 prose-h3:mb-1'>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

type MarkdownTypewriterProps = {
  markdown: string;
  speed?: number;
  className?: string;
  enable?: boolean;
  useContext?: boolean; // Whether to use TypewriterProvider context (optional)
  onTagChange?: (selectedValue: string, allSelected: string[]) => void;
  selectedTags?: string[];
};

export function MarkdownTypewriter({
  markdown,
  speed = 20,
  className = '',
  enable = true,
  useContext: useTypewriterContextProp = true,
  onTagChange,
  selectedTags,
}: MarkdownTypewriterProps) {
  const [index, setIndex] = useState(0);
  const [active, setActive] = useState(false);
  const typewriterContext = useOptionalTypewriterContext();

  useEffect(() => {
    setIndex(0);
    // Only set active to false if using typewriter context
    // Otherwise, immediately re-activate for the new markdown
    if (typewriterContext && useTypewriterContextProp) {
      setActive(false);
    } else {
      setActive(true);  // Re-activate immediately when no context
    }
  }, [markdown, typewriterContext, useTypewriterContextProp]);

  // Register with typewriter context if available
  useEffect(() => {
    if (typewriterContext && useTypewriterContextProp) {
      const order = typewriterContext.register(() => setActive(true));
      if (order === 0) setActive(true); // first one starts automatically
    } else {
      // If no context, start immediately
      setActive(true);
    }
  }, [typewriterContext, useTypewriterContextProp]);

  // Typewriter effect
  useEffect(() => {
    if (!enable) {
      setIndex(markdown.length);
      return;
    }

    if (!active) return;
    if (index < markdown.length) {
      // Reveal in chunks, not one char per render. Every increment re-renders
      // (and re-parses the partial markdown), so capping the number of renders
      // is what keeps the animation fast on long messages. Paired with the
      // memoized full render below, this takes a ~180-char message from ~45s to
      // ~1-2s. `speed` stays the per-step delay.
      const step = Math.max(1, Math.ceil(markdown.length / 80));
      const timeout = setTimeout(() => {
        setIndex(prev => Math.min(markdown.length, prev + step));
      }, speed);
      return () => clearTimeout(timeout);
    } else {
      // Trigger next typewriter if context is available
      if (typewriterContext && useTypewriterContextProp) {
        typewriterContext.next();
      }
    }
  }, [
    active,
    index,
    markdown,
    speed,
    enable,
    typewriterContext,
    useTypewriterContextProp,
  ]);

  // Local state for tag selections (if not controlled via props)
  const [localSelectedTags, setLocalSelectedTags] = useState<string[]>(
    selectedTags || []
  );

  // Use controlled or uncontrolled state
  const currentSelectedTags =
    selectedTags !== undefined ? selectedTags : localSelectedTags;

  // Handle tag click
  const handleTagClick = (tagValue: string, updatedTags: string[]) => {
    // Update local state if uncontrolled
    if (selectedTags === undefined) {
      setLocalSelectedTags(updatedTags);
    }

    // Call onChange callback
    if (onTagChange) {
      onTagChange(tagValue, updatedTags);
    }
  };

  // Create components with tag support
  const components = {
    ...baseMarkdownComponents,
    tag: createTagComponent(currentSelectedTags, handleTagClick),
  };

  // Sync local state when selectedTags prop changes
  useEffect(() => {
    if (selectedTags !== undefined) {
      setLocalSelectedTags(selectedTags);
    }
  }, [selectedTags]);

  // Parse the FULL message exactly once — it never changes during the reveal.
  // This invisible copy only reserves vertical space; re-parsing it on every
  // character was the dominant cost behind the slow typewriter.
  const fullRender = useMemo(
    () => (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markdown, currentSelectedTags]
  );

  // Get the partial markdown string
  const partialMarkdown = markdown.slice(0, index);
  const isAnimating = index < markdown.length;

  return (
    <div className={`prose prose-neutral max-w-none overflow-visible break-words prose-h1:text-xl prose-h1:font-semibold prose-h2:text-lg prose-h2:font-semibold prose-h3:text-base prose-h3:font-semibold prose-h1:mb-2 prose-h2:mb-1 prose-h3:mb-1 ${className}`}>
      {/* Use relative positioning to reserve space and prevent truncation */}
      <div className='relative'>
        {/* Invisible full content to reserve vertical space during animation */}
        {isAnimating && (
          <div className='invisible' aria-hidden='true'>
            {fullRender}
          </div>
        )}
        {/* Visible partial content - positioned absolutely during animation to overlay the space */}
        <div className={isAnimating ? 'absolute top-0 left-0 right-0' : ''}>
          <ReactMarkdown
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            components={components}
          >
            {partialMarkdown}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
